import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { sportShort } from "@/lib/sport";
import { useAuth, useFavorites } from "@/lib/auth";
import SportBadge from "@/components/SportBadge";
import { SectionHeading, Reveal } from "@/components/Section";

export default function TeamDirectory() {
  const [teams, setTeams] = useState([]);
  const [liveSlugs, setLiveSlugs] = useState(new Set());
  const { user } = useAuth();
  const { favorites } = useFavorites(user);

  useEffect(() => {
    api.get("/teams").then(({ data }) => setTeams(data.teams || [])).catch(() => {});
    api.get("/home/scoreboards")
      .then(({ data }) => setLiveSlugs(new Set((data.teams || []).filter((t) => t.game?.status?.state === "in").map((t) => t.slug))))
      .catch(() => {});
  }, []);

  const byLeague = teams.reduce((m, t) => {
    (m[t.league] = m[t.league] || []).push(t);
    return m;
  }, {});

  return (
    <section id="teams" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-20 sm:px-8 lg:px-12" data-testid="teams-directory">
      <SectionHeading index="07" eyebrow="THE FULL ROSTER" title="All 31 DFW Teams" />
      <div className="space-y-10">
        {Object.entries(byLeague).map(([league, list]) => {
          const sorted = [...list].sort((a, b) => (favorites.includes(b.slug) ? 1 : 0) - (favorites.includes(a.slug) ? 1 : 0));
          const isCollege = league === "NCAA" || league === "NAIA";
          // college: cluster each school's football / men's / women's teams together
          const bySchool = {};
          if (isCollege) {
            const sportRank = (t) => (t.sport_label.includes("Football") ? 0 : t.sport_label.includes("(W)") ? 2 : 1);
            sorted.forEach((t) => (bySchool[t.name] = bySchool[t.name] || []).push(t));
            Object.values(bySchool).forEach((arr) => arr.sort((a, b) => sportRank(a) - sportRank(b)));
          }
          const schools = Object.entries(bySchool).sort(([a], [b]) => a.localeCompare(b));
          const card = (t) => (
            <Link
              key={t.slug}
              to={`/team/${t.slug}`}
              data-testid={`directory-team-${t.slug}`}
              className="group flex items-center gap-3 rounded-lg border border-bline bg-surface px-4 py-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-volt/50"
            >
              {t.logo ? (
                <img src={t.logo} alt="" data-testid={`directory-logo-${t.slug}`} className="h-9 w-9 shrink-0 object-contain" />
              ) : (
                <span className="h-8 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: t.color }} />
              )}
              <span className="min-w-0">
                <span className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-bold text-white group-hover:text-volt">{t.name}</span>
                  <SportBadge sportLabel={t.sport_label} testid={`dir-sport-badge-${t.slug}`} />
                </span>
                <span className="block font-score text-[10px] tracking-widest text-slate-500">{sportShort(t.sport_label).toUpperCase()}</span>
              </span>
              {favorites.includes(t.slug) && (
                <Star data-testid={`directory-fav-${t.slug}`} className={`h-3.5 w-3.5 shrink-0 fill-volt text-volt ${liveSlugs.has(t.slug) ? "" : "ml-auto"}`} />
              )}
              {liveSlugs.has(t.slug) && (
                <span data-testid={`directory-live-${t.slug}`} className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-sm bg-liversed/15 px-1.5 py-0.5 font-score text-[9px] font-bold text-liversed">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-liversed" /> LIVE
                </span>
              )}
            </Link>
          );
          return (
            <Reveal key={league}>
              <p className="mb-4 font-score text-xs tracking-[0.3em] text-volt">{league}</p>
              {isCollege ? (
                <div className="space-y-6">
                  {schools.map(([school, members]) => (
                    <div key={school} data-testid={`school-group-${school.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <p className="mb-2 flex items-center gap-2 font-score text-[10px] tracking-[0.25em] text-slate-500">
                        {members[0].logo && <img src={members[0].logo} alt="" className="h-4 w-4 object-contain" />}
                        {school.toUpperCase()}
                      </p>
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{members.map(card)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">{sorted.map(card)}</div>
              )}
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}
